import type { ActionType, ProColumns } from '@ant-design/pro-components';
import { ProTable } from '@ant-design/pro-components';
import { useRequest } from 'ahooks';
import {
  Button,
  Descriptions,
  Drawer,
  message,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';
import { useRef, useState } from 'react';
import {
  ExamAdminDetailDTO,
  ExamAdminItemDTO,
  ExamAttemptAdminDTO,
  ExamAttemptStatusEnum,
  ExamStatusEnum,
  examController,
} from '../../api';
import { useCompany } from '../../hooks/company';
import {
  examStatusMapping,
  formatDuration,
  optionLetter,
  QuestionTypeTag,
} from './constants';
import ExamCreateWizard from './ExamCreateWizard';

// 考试详情抽屉：管理视角，含题目与正确答案
const ExamDetailDrawer = ({
  examId,
  onClose,
}: {
  examId: I.ID;
  onClose: () => void;
}) => {
  const { id: companyId } = useCompany();
  const { data } = useRequest(() => examController.get(companyId, examId), {
    onError: (e) => message.error(e.message),
  });

  return (
    <Drawer title={data?.title || '考试详情'} open width={720} onClose={onClose}>
      {data && (
        <>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="状态">
              {data.status ? examStatusMapping[data.status] : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="锁卷">
              {data.locked ? '已有人作答（锁卷）' : '未开始作答'}
            </Descriptions.Item>
            <Descriptions.Item label="总分">{data.totalScore}</Descriptions.Item>
            <Descriptions.Item label="及格线">{data.passScore}</Descriptions.Item>
            <Descriptions.Item label="应考人数">
              {data.requiredCount}
            </Descriptions.Item>
            <Descriptions.Item label="已交卷人数">
              {data.submittedUserCount}
            </Descriptions.Item>
            <Descriptions.Item label="开始时间">
              {data.startAt || '不限'}
            </Descriptions.Item>
            <Descriptions.Item label="截止时间">
              {data.endAt || '不限'}
            </Descriptions.Item>
            <Descriptions.Item label="限时">
              {data.durationMinutes ? `${data.durationMinutes} 分钟` : '不限时'}
            </Descriptions.Item>
            <Descriptions.Item label="允许作答次数">
              {data.maxAttempts}
            </Descriptions.Item>
          </Descriptions>
          <Table
            style={{ marginTop: 16 }}
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={data.questions}
            columns={[
              { title: '#', dataIndex: 'seq', width: 50 },
              {
                title: '题型',
                width: 90,
                render: (_, record) => <QuestionTypeTag type={record.type} />,
              },
              { title: '题干', dataIndex: 'content', ellipsis: true },
              {
                title: '选项',
                ellipsis: true,
                render: (_, record) =>
                  (record.options || [])
                    .map((item, index) => `${optionLetter(index)}.${item}`)
                    .join(' '),
              },
              { title: '答案', dataIndex: 'correctAnswer', width: 90 },
              { title: '分值', dataIndex: 'score', width: 60 },
            ]}
          />
        </>
      )}
    </Drawer>
  );
};

// 全部答卷抽屉：每人每次作答记录
const AttemptsDrawer = ({
  exam,
  onClose,
}: {
  exam: ExamAdminItemDTO;
  onClose: () => void;
}) => {
  const { id: companyId } = useCompany();

  const columns: ProColumns<ExamAttemptAdminDTO>[] = [
    { title: '姓名', dataIndex: 'userName' },
    { title: '部门', dataIndex: 'departmentName' },
    { title: '第几次', dataIndex: 'attemptNo', width: 80 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (_, record) =>
        record.status === ExamAttemptStatusEnum.SUBMITTED ? (
          <Tag color="green">已交卷</Tag>
        ) : (
          <Tag color="orange">作答中</Tag>
        ),
    },
    { title: '得分', dataIndex: 'score', width: 70 },
    {
      title: '是否通过',
      dataIndex: 'pass',
      width: 90,
      render: (_, record) =>
        record.pass === undefined || record.pass === null ? (
          '-'
        ) : record.pass ? (
          <Tag color="green">通过</Tag>
        ) : (
          <Tag color="red">未通过</Tag>
        ),
    },
    {
      title: '用时',
      dataIndex: 'durationSeconds',
      width: 100,
      render: (_, record) => formatDuration(record.durationSeconds),
    },
    {
      title: '最高分',
      dataIndex: 'isBest',
      width: 80,
      render: (_, record) => (record.isBest ? <Tag color="gold">最高</Tag> : '-'),
    },
    {
      title: '超时自动交卷',
      dataIndex: 'isAutoSubmit',
      width: 110,
      render: (_, record) => (record.isAutoSubmit ? '是' : '否'),
    },
    { title: '交卷时间', dataIndex: 'submittedAt', width: 160 },
  ];

  return (
    <Drawer title={`答卷记录：${exam.title}`} open width={960} onClose={onClose}>
      <ProTable<ExamAttemptAdminDTO>
        rowKey="attemptId"
        columns={columns}
        search={false}
        options={false}
        pagination={{ pageSize: 20 }}
        request={async ({ current, pageSize }) => {
          const data = await examController.listAttempts(
            companyId,
            exam.id as I.ID,
            current,
            pageSize,
          );
          return { data: data.list, success: true, total: data.total };
        }}
      />
    </Drawer>
  );
};

export default () => {
  const { id: companyId, hasPermission } = useCompany();
  const ref = useRef<ActionType>();
  const [wizard, setWizard] = useState<{
    open: boolean;
    exam?: ExamAdminDetailDTO;
  }>({ open: false });
  const [detailExamId, setDetailExamId] = useState<I.ID>();
  const [attemptsExam, setAttemptsExam] = useState<ExamAdminItemDTO>();

  const canEdit = hasPermission('page:exam:manage:edit');

  const openEdit = async (examId: I.ID) => {
    try {
      const detail = await examController.get(companyId, examId);
      if (detail.locked) {
        message.warning('已有人作答，仅可延长截止时间和修改说明');
      }
      setWizard({ open: true, exam: detail });
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const columns: ProColumns<ExamAdminItemDTO>[] = [
    {
      title: '考试名称',
      dataIndex: 'title',
      ellipsis: true,
      search: { transform: (value) => ({ keyword: value }) },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (_, record) =>
        record.status === ExamStatusEnum.PUBLISHED ? (
          <Tag color="green">已发布</Tag>
        ) : (
          <Tag>已关闭</Tag>
        ),
      renderFormItem: () => (
        <Select
          allowClear
          options={Object.entries(examStatusMapping).map(([value, label]) => ({
            value,
            label,
          }))}
        />
      ),
    },
    { title: '题数', dataIndex: 'questionCount', width: 70, hideInSearch: true },
    { title: '总分', dataIndex: 'totalScore', width: 70, hideInSearch: true },
    { title: '及格线', dataIndex: 'passScore', width: 80, hideInSearch: true },
    {
      title: '限时',
      dataIndex: 'durationMinutes',
      width: 90,
      hideInSearch: true,
      render: (_, record) =>
        record.durationMinutes ? `${record.durationMinutes} 分钟` : '不限',
    },
    {
      title: '可考次数',
      dataIndex: 'maxAttempts',
      width: 90,
      hideInSearch: true,
    },
    {
      title: '考后可见答案',
      dataIndex: 'showAnswer',
      width: 110,
      hideInSearch: true,
      render: (_, record) => (
        <Switch
          size="small"
          checked={!!record.showAnswer}
          disabled={!canEdit}
          onChange={async (checked) => {
            try {
              // 独立开关端点：未锁卷/锁卷/已关闭的考试都可切换，不走编辑校验
              await examController.setShowAnswer(
                companyId,
                record.id as I.ID,
                checked,
              );
              message.success(checked ? '已开启考后可见答案' : '已关闭考后可见答案');
              ref.current?.reload();
            } catch (e: any) {
              message.error(e.message);
            }
          }}
        />
      ),
    },
    { title: '开始时间', dataIndex: 'startAt', width: 150, hideInSearch: true },
    { title: '截止时间', dataIndex: 'endAt', width: 150, hideInSearch: true },
    {
      title: '应考/交卷',
      width: 100,
      hideInSearch: true,
      render: (_, record) =>
        `${record.requiredCount ?? '-'} / ${record.submittedUserCount ?? '-'}`,
    },
    {
      title: '操作',
      valueType: 'option',
      width: 220,
      render: (_, record) => {
        const actions = [
          <a key="detail" onClick={() => setDetailExamId(record.id as I.ID)}>
            详情
          </a>,
          <a key="attempts" onClick={() => setAttemptsExam(record)}>
            答卷
          </a>,
        ];
        if (canEdit && record.status === ExamStatusEnum.PUBLISHED) {
          actions.push(
            <a key="edit" onClick={() => openEdit(record.id as I.ID)}>
              编辑
            </a>,
            <Popconfirm
              key="close"
              title="关闭后将不能再作答，进行中的答卷会自动交卷，确认？"
              onConfirm={async () => {
                try {
                  await examController.close(companyId, record.id as I.ID);
                  message.success('已关闭');
                  ref.current?.reload();
                } catch (e: any) {
                  message.error(e.message);
                }
              }}
            >
              <a>关闭</a>
            </Popconfirm>,
          );
        }
        if (canEdit) {
          actions.push(
            <Popconfirm
              key="remove"
              title="仅无人作答的考试可删除，确认？"
              onConfirm={async () => {
                try {
                  await examController.remove(companyId, record.id as I.ID);
                  message.success('删除成功');
                  ref.current?.reload();
                } catch (e: any) {
                  message.error(e.message);
                }
              }}
            >
              <a>删除</a>
            </Popconfirm>,
          );
        }
        return <Space size="small">{actions}</Space>;
      },
    },
  ];

  return (
    <>
      <ProTable<ExamAdminItemDTO>
        actionRef={ref}
        rowKey="id"
        columns={columns}
        scroll={{ x: 1400 }}
        pagination={{ pageSizeOptions: [20, 50, 100] }}
        request={async ({ current, pageSize, ...params }) => {
          const data = await examController.list(companyId, current, pageSize, {
            status: (params as any).status,
            keyword: (params as any).keyword,
          });
          return { data: data.list, success: true, total: data.total };
        }}
        headerTitle="考试管理"
        toolBarRender={() =>
          canEdit
            ? [
                <Button
                  key="create"
                  type="primary"
                  onClick={() => setWizard({ open: true })}
                >
                  新建考试
                </Button>,
              ]
            : []
        }
      />
      {wizard.open && (
        <ExamCreateWizard
          open={wizard.open}
          exam={wizard.exam}
          onClose={(refresh) => {
            setWizard({ open: false });
            if (refresh) {
              ref.current?.reload();
            }
          }}
        />
      )}
      {detailExamId && (
        <ExamDetailDrawer
          examId={detailExamId}
          onClose={() => setDetailExamId(undefined)}
        />
      )}
      {attemptsExam && (
        <AttemptsDrawer
          exam={attemptsExam}
          onClose={() => setAttemptsExam(undefined)}
        />
      )}
    </>
  );
};
